'use client'

import { formatDate_yyyy_mm_dd } from '@/lib/util'
import {
  Box,
  IconButton,
  SimpleGrid,
  Text,
  Image,
  Flex,
  Button,
  Spinner,
  keyframes,
} from '@chakra-ui/react'
import Link from 'next/link'
import { useState } from 'react'
import { IoCalendarOutline } from 'react-icons/io5'
import { GrView } from 'react-icons/gr'

export default function MegazineCard({
  megazine,
  index,
}: {
  megazine: any
  index: number
}) {
  const [isLoading, setIsLoading] = useState(false)
  const handleCardClick = () => {
    setIsLoading(true)
  }

  // NEW 아이콘 애니메이션 정의
  const rollAnimation = keyframes`
    0% {
      transform: translateX(0) rotate(330deg);
    }
    30% {
      transform: translateX(0) rotate(330deg); // 처음 3초 동안 대기 (30% = 3초)
    }
    80% {
      transform: translateX(-120px) rotate(-180deg);
    }
    100% {
      transform: translateX(0) rotate(330deg);
    }
  `

  // 순차적 지연 시간 설정 (index 값을 활용하여 각 카드의 delay 적용)
  const animationDelay = `${index * 0.2}s` // 각 카드가 1초 간격으로 딜레이

  // 박스 안에 글자
  // TOP (SUMMER)
  const moveLeftRight = keyframes`
  0% {
    transform: translate(-100px, 0); // 처음 위치
  }
  50% {
    transform: translate(210px, 0); // 중간 위치
  }
  66.67% {
    transform: translate(210px, 0); // 50%에서 66.67%까지 멈춤
  }
  100% {
    transform: translate(600px, 0); // 원래 위치로 복귀
  }
` // BOTTOM (FOREST)
  const moveRightLeft = keyframes`
  0% {
    transform: translate(600px, 0); // 처음 위치
  }
  50% {
    transform: translate(210px, 0); // 중간 위치
  }
  66.67% {
    transform: translate(210px, 0); // 50%에서 66.67%까지 멈춤
  }
  100% {
    transform: translate(-100px, 0); // 원래 위치로 복귀
  }
`
  // RIGHT (NAUTRE)
  const moveTopDown = keyframes`
  0% {
    transform: translate(0, -100px); // 처음 위치
  }
  50% {
    transform: translate(0, 230px); // 중간 위치
  }
  66.67% {
    transform: translate(0, 230px); // 50%에서 66.67%까지 멈춤
  }
  100% {
    transform: translate(0, 600px); // 원래 위치로 복귀
  }
`
  // LEFT (GARDEN)
  const moveDownTop = keyframes`
  0% {
    transform: translate(0, 600px); // 처음 위치
  }
  50% {
    transform: translate(0, 230px); // 중간 위치
  }
  66.67% {
    transform: translate(0, 230px); // 50%에서 66.67%까지 멈춤
  }
  100% {
    transform: translate(0, -100px); // 원래 위치로 복귀
  }
`

  return (
    <>
      <Box
        maxW={500}
        as={Link} // GridItem을 Link로 사용
        onClick={handleCardClick}
        p={{ base: 4, md: 0 }}
        href={`/megazines/${megazine?.itemId}`} // 링크 주소
        cursor={isLoading ? 'wait' : 'pointer'}>
        {/* NEW 아이콘 버튼 */}
        {(index === 0 || index === 1) && (
          <IconButton
            width="35px"
            bgColor={'transparent'}
            h="20px"
            aria-label="New"
            left={{ base: '92%', md: '94%' }}
            mb={2}
            animation={`${rollAnimation} 6s ${animationDelay} infinite linear`} // 애니메이션에 지연 시간 추가
            icon={
              <Image
                src="/halym_emoji.png"
                alt="여름숲_로고"
                transform={`rotate(${35}deg)`}
                width={35}
                height={35}
                loading="lazy"
                fit={'contain'}
                rounded={'100%'}
              />
            }
          />
        )}
        <Box
          rounded={24}
          border={'1px solid black'}
          position={'relative'}
          overflow={'hidden'}
          role="group" // 그룹 역할을 명시
        >
          <Image
            rounded={24}
            zIndex={1}
            loading="lazy"
            src={megazine?.thumbnail}
            alt={`여름숲`}
            width={{ base: '100%', md: '500px' }} // 모바일에서 100% 너비
            height={{ base: '300px', md: '500px' }} // 모바일에서 고정된 높이
            objectFit="cover" // 이미지 비율 유지
            _hover={{
              md: {
                transform: 'scale(0.85)', // 마우스 호버 시 축소
                transition: 'transform 0.5s ease', // 애니메이션 설정
              },
            }}
            style={{
              transition: 'transform 0.5s ease', // 이미지가 원래 크기로 복구될 때도 동일한 애니메이션 적용
            }}
          />
          <Box
            position="absolute"
            zIndex={0}
            top={0} // 이미지 중앙에 위치
            opacity={0} // 기본적으로 보이지 않음
            transform="translate(-50%, 0%)" // 중앙 정렬을 위한 변환
            transition="opacity 0.5s ease" // opacity 변경 애니메이션
            _groupHover={{
              base: {
                opacity: 0, // 그룹 전체에 hover 시 나타남
              },
              md: {
                opacity: 1, // 그룹 전체에 hover 시 나타남
              },
            }}
            animation={`${moveLeftRight} 4s infinite ease-in-out`}
            color="black" // 텍스트 색상
            style={{ animationDelay: '0s' }}
            borderRadius={8} // 모서리 둥글게
          >
            {/* TOP */}
            <Text
              fontSize={14}
              fontWeight={700}
              p={2}>
              SUMMER
            </Text>
          </Box>
          <Box
            position="absolute"
            zIndex={0}
            top={0} // 이미지 상단에 위치
            right={'-2%'}
            opacity={0} // 기본적으로 보이지 않음
            transform="translate(-50%, 0%)" // 회전과 함께 중앙 정렬
            transition="opacity 0.5s ease" // opacity 변경 애니메이션
            _groupHover={{
              base: {
                opacity: 0, // 그룹 전체에 hover 시 나타남
              },
              md: {
                opacity: 1, // 그룹 전체에 hover 시 나타남
              },
            }}
            animation={`${moveTopDown} 4s infinite ease-in-out`}
            style={{ animationDelay: '0s' }}
            color="black" // 텍스트 색상
            borderRadius={8} // 모서리 둥글게
          >
            {/* RIGHT */}
            <Text
              transform="rotate(90deg)"
              fontSize={14}
              fontWeight={700}>
              NAUTRE
            </Text>{' '}
          </Box>

          {/* BOTTOM 텍스트가 이미지 아래에 겹치도록 설정 */}
          <Box
            position="absolute"
            zIndex={0}
            bottom={0} // 이미지 중앙에 위치
            opacity={0} // 기본적으로 보이지 않음
            transform="translate(-50%, 0%)" // 중앙 정렬을 위한 변환
            transition="opacity 0.5s ease" // opacity 변경 애니메이션
            _groupHover={{
              base: {
                opacity: 0, // 그룹 전체에 hover 시 나타남
              },
              md: {
                opacity: 1, // 그룹 전체에 hover 시 나타남
              },
            }}
            animation={`${moveRightLeft} 4s infinite ease-in-out`}
            color="black" // 텍스트 색상
            style={{ animationDelay: '0s' }}
            borderRadius={8} // 모서리 둥글게
          >
            {/* BOTTOM */}
            <Text
              p={2}
              fontSize={14}
              fontWeight={700}>
              FOREST
            </Text>
          </Box>

          <Box
            position="absolute"
            zIndex={0}
            top={0} // 이미지 상단에 위치
            left={'-2%'}
            opacity={0} // 기본적으로 보이지 않음
            transform="translate(-50%, 0%) " // 회전과 함께 중앙 정렬
            transition="opacity 0.5s ease" // opacity 변경 애니메이션
            _groupHover={{
              base: {
                opacity: 0, // 그룹 전체에 hover 시 나타남
              },
              md: {
                opacity: 1, // 그룹 전체에 hover 시 나타남
              },
            }}
            animation={`${moveDownTop} 4s infinite ease-in-out`}
            style={{ animationDelay: '0s' }}
            color="black" // 텍스트 색상
            borderRadius={8} // 모서리 둥글게
          >
            {/* LEFT */}
            <Text
              transform="rotate(270deg)"
              fontSize={14}
              fontWeight={700}>
              GARDEN
            </Text>{' '}
          </Box>
        </Box>
        <Box
          px={4}
          pt={4}
          pb={2}>
          <Text
            fontSize={{ base: 20, md: 24 }}
            maxW="512px"
            h={'40px'}
            color="main-textColor"
            fontWeight="bold"
            mb={2}>
            {megazine?.title.length > 25
              ? `${megazine?.title.slice(0, 25)}..`
              : megazine?.title}
            {isLoading && (
              <Flex
                justifyContent="center"
                alignItems="center"
                mt={4}>
                <Spinner color="orange.500" />
              </Flex>
            )}
          </Text>

          <Text
            maxW={{ base: '400px', md: '500px' }}
            fontSize={14}
            h={'60px'}
            lineHeight="20px"
            sx={{
              overflow: 'hidden', // 넘치는 내용을 숨김
              textOverflow: 'ellipsis', // 넘치는 텍스트를 생략 부호로 표시
            }}>
            {/* HTML 태그 제거 후, 150자까지 사용하고 150자를 넘을 경우 '...' 추가 */}
            {megazine?.forumContent.replace(/(<([^>]+)>)/gi, '').length > 150
              ? `${megazine?.forumContent
                  .replace(/(<([^>]+)>)/gi, '')
                  .slice(0, 150)}...`
              : megazine?.forumContent.replace(/(<([^>]+)>)/gi, '')}
          </Text>
        </Box>

        {/* 버튼과 날짜 영역 */}

        {/* <Flex
          maxW={{ base: '100%', md: '500px' }} // 부모 Flex 요소에 최대 너비 설정
        >
          <Box
            minW={{ base: '220px', md: '350px' }}
            h="48px"
            fontSize={16}
            px={8}
            backgroundColor={'#E6F7FC'}
            display="flex"
            textAlign={'center'}
            alignContent={'center'}
            justifyContent="start"
            alignItems="center">
            <Flex
              justifyContent={'center'}
              alignItems={'center'}>
              <IoCalendarOutline />⠀{formatDate_yyyy_mm_dd(megazine?.createdAt)}
            </Flex>
          </Box>

          <Button
            flexGrow={1} // 남은 공간을 채우도록 설정
            h="48px"
            fontWeight={600}
            rounded={0}
            colorScheme="blue">
            More ＞
          </Button>
        </Flex> */}
      </Box>
    </>
  )
}
